import { makeAutoObservable } from 'mobx'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { ScheduleDelayStore } from 'shared/ui/Schedule/ui/ScheduleDelay'
import localDate from 'shared/lib/dayjs'
import { type ICampaignScheduleDateField, type IWhenDateFieldPeriod } from 'entities/Campaign'
import type { CustomField } from 'entities/CustomField'

export class ScheduleDateFiledStore {
  scheduleDelayStore
  dateField: CustomField | null = null
  constructor() {
    this.scheduleDelayStore = new ScheduleDelayStore({
      value: '3',
      unit: 'days',
    })
    makeAutoObservable(this)
  }

  get use_contact_timezone() {
    if (this.dateFiledType === 'date') {
      return this._isUseContactTimezone
    }
    if (this.dateFiledType === 'datetime' && !this.isUseTimeFromField) {
      return this._isUseContactTimezone
    }
    return false
  }

  get use_time_from_field() {
    if (this.dateFiledType === 'datetime') {
      return this.isUseTimeFromField
    }
    return false
  }

  get params(): ICampaignScheduleDateField['options'] {
    return {
      interval_type: this.when,
      interval_unit: this.scheduleDelayStore.params.unit,
      interval_value: this.scheduleDelayStore.params.value,
      date_field: this.dateFiledKey,
      time: this.dateFiledType === 'date' ? this.apiDateFieldTime : this.apiDatetimeFieldTime,
      use_time_from_field: this.use_time_from_field,
      use_contact_timezone: this.use_contact_timezone,
    }
  }

  get dateFiledType() {
    return this.dateField?.type
  }

  get dateFiledKey() {
    return this.dateField?.key
  }

  get dateFieldScheduleText() {
    if (!this.dateField) {
      return ''
    }
    if (this.dateFiledType === 'date') {
      return `${this.dateField?.name} at ${this.uiDateFieldTime}`
    }
    if (this.dateFiledType === 'datetime') {
      if (this.isUseTimeFromField) {
        return this.dateField?.name
      }
      return `${this.dateField?.name} at ${this.uiDatetimeFieldTime}`
    }
  }

  get whenScheduleText() {
    if (this.when === 'on') {
      return ''
    }
    return this.scheduleDelayStore.scheduleText
  }

  get scheduleText() {
    return `${this.whenScheduleText} ${this.when} ${this.dateFieldScheduleText}`
  }

  init = async ({
    use_time_from_field,
    time,
    use_contact_timezone,
    interval_value,
    interval_unit,
    interval_type,
    default_timezone,
    dateField,
  }: ICampaignScheduleDateField['options'] & {
    default_timezone: string
    dateField: CustomField | null
  }) => {
    this.when = interval_type
    this.scheduleDelayStore.init({
      unit: interval_unit,
      value: interval_value,
    })
    this._isUseContactTimezone = use_contact_timezone
    if (dateField) this.dateField = dateField

    const split = time?.split(':')
    const hours = Number(split?.[0])
    const minutes = Number(split?.[1])

    if (this.dateFiledType === 'date') {
      this.dateFieldDate = localDate(new Date(), default_timezone)
        .startOf('day')
        .add(hours, 'hours')
        .add(minutes, 'minutes')
        .add(uiStore.offsetMinute, 'minutes')
        .toDate()
    }
    if (this.dateFiledType === 'datetime') {
      this.isUseTimeFromField = use_time_from_field
      if (!use_time_from_field) {
        this.datetimeFieldDate = localDate(new Date(), default_timezone)
          .startOf('day')
          .add(hours, 'hours')
          .add(minutes, 'minutes')
          .add(uiStore.offsetMinute, 'minutes')
          .toDate()
      }
    }
  }

  when: IWhenDateFieldPeriod = 'before'
  setWhen = (when: IWhenDateFieldPeriod) => {
    if (when === 'on' && this.dateFiledType === 'datetime') {
      this.setUseTimeFromField(true)
    }
    this.when = when
  }

  dateFieldDate: Date | null = uiStore.dayjs().startOf('day').add(9, 'hours').toDate()
  get uiDateFieldDate() {
    return uiStore.dayjs(this.dateFieldDate).add(-uiStore.offsetMinute, 'minutes')
  }
  get uiDateFieldTime() {
    return this.uiDateFieldDate.format(DayjsFormats.time)
  }
  get apiDateFieldTime() {
    return this.uiDateFieldDate.format(DayjsFormats.time4)
  }
  setDateFieldDate = (date: Date | null) => {
    this.dateFieldDate = date
  }

  datetimeFieldDate: Date | null = null
  get uiDatetimeFieldDate() {
    if (!this.datetimeFieldDate) return null
    return uiStore.dayjs(this.datetimeFieldDate).add(-uiStore.offsetMinute, 'minutes')
  }
  get uiDatetimeFieldTime() {
    if (!this.uiDatetimeFieldDate) {
      return null
    }
    return this.uiDatetimeFieldDate.format(DayjsFormats.time)
  }
  get apiDatetimeFieldTime() {
    if (!this.uiDatetimeFieldDate) {
      return null
    }
    return this.uiDatetimeFieldDate.format(DayjsFormats.time4)
  }
  setDatetimeFieldDate = (date: Date | null) => {
    if (date) {
      this.isUseTimeFromField = false
      this.datetimeFieldDate = date
    } else {
      this.isUseTimeFromField = true
    }
  }

  private _isUseContactTimezone = false
  get isUseContactTimezone() {
    if (this.dateFiledType === 'date') {
      return this._isUseContactTimezone
    }
    if (this.dateFiledType === 'datetime') {
      if (this.isUseTimeFromField) {
        return null
      }
      return this._isUseContactTimezone
    }
    return null
  }
  toggleUseContactTimezone = () => {
    this._isUseContactTimezone = !this._isUseContactTimezone
  }

  isUseTimeFromField = false
  setUseTimeFromField = (isUseTimeFromField: boolean) => {
    if (isUseTimeFromField) {
      this.datetimeFieldDate = null
    }
    this.isUseTimeFromField = isUseTimeFromField
  }

  get isMinutesOrHoursPeriod() {
    return (
      this.scheduleDelayStore.schedulePeriodStore.period === 'minutes' ||
      this.scheduleDelayStore.schedulePeriodStore.period === 'hours'
    )
  }

  get isNoMinutesAndHoursPeriods() {
    if (this.dateFiledType === 'date') {
      return true
    }
    if (this.dateFiledType === 'datetime' && !this.params.use_time_from_field) {
      return true
    }
    return false
  }

  onSetDateField = (dateFiled: CustomField) => {
    if (dateFiled.type === 'datetime') {
      this.setUseTimeFromField(true)
    }
    if (dateFiled.type === 'date') {
      this.setUseTimeFromField(false)
    }
  }
}
